import { useEffect, useMemo, useState } from 'react'
import { check, loadClassifications } from '../classifications'
import type { Classifications } from '../classifications'
import { errorMessage } from '../pb'
import type { RecipeIngredient } from '../recipes'
import {
  CATEGORY_OPTIONS,
  DIFFICULTY_OPTIONS,
  categoryLabel,
  checkSummary,
  submitRecipe,
} from '../submitRecipe'

const EMPTY_ROW: RecipeIngredient = { amount: '', unit: '', name: '' }

/**
 * Share a recipe with the sisterhood.
 *
 * The Leviticus 11 check runs as she types, and it is free: knowing whether a food is clean is
 * observance, and observance is never behind the paywall. It is also never a scolding. A
 * questioned ingredient is named, explained, and cited, so she can see the text for herself and
 * decide, rather than being told no by a machine.
 *
 * Only REJECTED stops a submission. NEEDS_SOURCE goes through: unspecified gelatin genuinely is
 * not saying pig or cow, and refusing her recipe over it would claim knowledge nobody has.
 */
export function SubmitRecipeForm({
  myUserId,
  onSubmitted,
}: {
  myUserId: string
  onSubmitted: () => void
}) {
  const [classifications, setClassifications] = useState<Classifications | null>(null)
  const [title, setTitle] = useState('')
  const [rows, setRows] = useState<RecipeIngredient[]>([{ ...EMPTY_ROW }, { ...EMPTY_ROW }])
  const [instructions, setInstructions] = useState('')
  const [servings, setServings] = useState('4')
  const [prepMinutes, setPrepMinutes] = useState('')
  const [cookMinutes, setCookMinutes] = useState('')
  const [difficulty, setDifficulty] = useState('')
  const [sabbathPrep, setSabbathPrep] = useState(false)
  const [categories, setCategories] = useState<string[]>([])
  const [nutrition, setNutrition] = useState({ cal: '', protein: '', carbs: '', fat: '', fiber: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    void loadClassifications().then(setClassifications)
  }, [])

  const named = rows.map((row) => row.name).filter((name) => name.trim().length > 0)

  // Pure and small, 54 rules against a handful of lines, so it runs on every keystroke rather
  // than behind a debounce. She sees the answer as she writes the ingredient, which is the
  // moment it is useful.
  const result = useMemo(
    () => check(named, classifications?.ingredients ?? []),
    [named.join(' '), classifications],
  )

  const blocked = result.verdict === 'REJECTED'
  const ready = title.trim().length > 0 && named.length > 0 && !blocked

  function setRow(index: number, patch: Partial<RecipeIngredient>) {
    setRows(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  function toggleCategory(value: string) {
    setCategories(
      categories.includes(value) ? categories.filter((c) => c !== value) : [...categories, value],
    )
  }

  const num = (raw: string): number | null => {
    const parsed = Number.parseInt(raw, 10)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }

  async function send(event: React.FormEvent) {
    event.preventDefault()
    if (!ready) return
    setBusy(true)
    setError(null)
    try {
      await submitRecipe(myUserId, {
        title,
        ingredients: rows.filter((row) => row.name.trim().length > 0),
        instructions,
        servings: num(servings) ?? 1,
        prepMinutes: num(prepMinutes) ?? 0,
        cookMinutes: num(cookMinutes) ?? 0,
        difficulty,
        sabbathPrep,
        categories,
        caloriesPerServing: num(nutrition.cal),
        proteinGrams: num(nutrition.protein),
        carbsGrams: num(nutrition.carbs),
        fatGrams: num(nutrition.fat),
        fiberGrams: num(nutrition.fiber),
        proteinTags: result.derivedProteinTags,
        cleanVerified: result.verdict === 'CLEAN',
      })
      setDone(true)
      onSubmitted()
    } catch (err) {
      setError(errorMessage(err, 'Could not send the recipe'))
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="card">
        <h3 className="panel-title">Sent for review</h3>
        <p className="panel-note">
          It will appear on the board once a sister has read it through. It is not in the list
          below yet, which is expected.
        </p>
      </div>
    )
  }

  return (
    <form className="card" onSubmit={send}>
      <h3 className="panel-title">Share a recipe</h3>
      <p className="panel-note">Checked against Leviticus 11 as you write.</p>

      {/* Split by what the fields are, not by how many there are: the recipe itself on the left,
          what it is for on the right. Stacked, this form was the longest scroll on the site. */}
      <div className="form-pair">
      <div className="stack">
        <div>
          <label className="field-label" htmlFor="title">
            What is it called
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div>
          <span className="field-label">Ingredients</span>
          {rows.map((row, index) => (
            <div className="ing-row" key={index}>
              <input
                type="text"
                aria-label={`Amount for ingredient ${index + 1}`}
                placeholder="2"
                value={row.amount}
                onChange={(e) => setRow(index, { amount: e.target.value })}
              />
              <input
                type="text"
                aria-label={`Unit for ingredient ${index + 1}`}
                placeholder="cups"
                value={row.unit}
                onChange={(e) => setRow(index, { unit: e.target.value })}
              />
              <input
                type="text"
                aria-label={`Ingredient ${index + 1}`}
                placeholder="beef broth"
                value={row.name}
                onChange={(e) => setRow(index, { name: e.target.value })}
              />
            </div>
          ))}
          <button
            className="btn-quiet"
            type="button"
            onClick={() => setRows([...rows, { ...EMPTY_ROW }])}
          >
            Add another ingredient
          </button>
        </div>

        <CleanCheck result={result} hasIngredients={named.length > 0} />

        <div>
          <label className="field-label" htmlFor="instructions">
            How it is made
          </label>
          <textarea
            id="instructions"
            className="prayer-input"
            rows={5}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />
        </div>
      </div>

      <div className="stack">
        <div className="field-grid">
          <div>
            <label className="field-label" htmlFor="servings">
              Serves
            </label>
            <input
              id="servings"
              type="text"
              inputMode="numeric"
              value={servings}
              onChange={(e) => setServings(e.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="prep">
              Prep, minutes
            </label>
            <input
              id="prep"
              type="text"
              inputMode="numeric"
              value={prepMinutes}
              onChange={(e) => setPrepMinutes(e.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="cook">
              Cook, minutes
            </label>
            <input
              id="cook"
              type="text"
              inputMode="numeric"
              value={cookMinutes}
              onChange={(e) => setCookMinutes(e.target.value)}
            />
          </div>
        </div>

        <div>
          <span className="field-label">How hard is it</span>
          <div className="chips">
            {DIFFICULTY_OPTIONS.map((option) => (
              <button
                key={option}
                className={difficulty === option ? 'chip chip-on' : 'chip'}
                type="button"
                aria-pressed={difficulty === option}
                onClick={() => setDifficulty(difficulty === option ? '' : option)}
              >
                {categoryLabel(option)}
              </button>
            ))}
          </div>
        </div>

        <label className="checkline">
          <input
            type="checkbox"
            checked={sabbathPrep}
            onChange={(e) => setSabbathPrep(e.target.checked)}
          />
          <span>This one keeps, so it can be made ahead for the Sabbath</span>
        </label>

        <div>
          <span className="field-label">When is it for</span>
          <div className="chips">
            {CATEGORY_OPTIONS.map((option) => (
              <button
                key={option}
                className={categories.includes(option) ? 'chip chip-on' : 'chip'}
                type="button"
                aria-pressed={categories.includes(option)}
                onClick={() => toggleCategory(option)}
              >
                {categoryLabel(option)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className="field-label">Nutrition per serving, if you know it</span>
          <div className="field-grid">
            {(
              [
                ['cal', 'Calories'],
                ['protein', 'Protein g'],
                ['carbs', 'Carbs g'],
                ['fat', 'Fat g'],
                ['fiber', 'Fibre g'],
              ] as const
            ).map(([key, label]) => (
              <div key={key}>
                <label className="field-label" htmlFor={`n-${key}`}>
                  {label}
                </label>
                <input
                  id={`n-${key}`}
                  type="text"
                  inputMode="numeric"
                  value={nutrition[key]}
                  onChange={(e) => setNutrition({ ...nutrition, [key]: e.target.value })}
                />
              </div>
            ))}
          </div>
        </div>

        {error && <p className="notice">{error}</p>}

        <button className="btn-primary" type="submit" disabled={!ready || busy}>
          {busy ? 'Sending' : 'Send for review'}
        </button>
      </div>
      </div>
    </form>
  )
}

/**
 * The check, said plainly.
 *
 * Every questioned ingredient is named with its reason and its citation, so she can read
 * Leviticus for herself. A verdict without the text behind it is just a machine saying no.
 */
function CleanCheck({
  result,
  hasIngredients,
}: {
  result: ReturnType<typeof check>
  hasIngredients: boolean
}) {
  if (!hasIngredients) return null

  const tone =
    result.verdict === 'REJECTED'
      ? 'check check-stop'
      : result.verdict === 'NEEDS_SOURCE'
        ? 'check check-ask'
        : 'check'

  return (
    <div className={tone}>
      <p className="check-summary">{checkSummary(result.verdict)}</p>

      {result.findings.length > 0 && (
        <ul className="findings">
          {result.findings.map((finding) => (
            <li key={`${finding.ingredientName}-${finding.ruleId}`}>
              <span className="finding-name">{finding.ingredientName}</span>
              <span className="finding-reason">{finding.reason}</span>
              {finding.references.length > 0 && (
                <span className="finding-refs">{finding.references.join(' · ')}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
